import {
    type View
} from "react-native";
import type {
    DateTimePickerTitleFormatConstantType,
    DateTimePickerPlaceholderConstantType,
    DateTimePickerRangeErrorConstantType,
    DateTimePickerSelectionConstantType,
    DateTimePickerRangeTextConstantType,
    DateTimePickerHasValueConstantType,
    DateTimePickerResponseConstantType,
    DateTimePickerDateTextConstantType,
    DateTimePickerResponseType,
    DateTimePickerPickerType,
    DateTimePickerValueType,
    DateTimePickerVariant,
    DateCalendarType,
    TimeToken
} from "./type";
import moment from "moment";
import {
    createMoment
} from "../../helpers/locale/format";

export const DATE_TIME_PICKER_DATE_FORMAT = "DD MMM YYYY";

export const DATE_TIME_PICKER_TIME_FORMATS: Record<"twentyFourHour" | "twelveHour", Record<"withSeconds" | "default", TimeToken>> = {
    twelveHour: {
        withSeconds: "hh:mm:ss A",
        default: "hh:mm A"
    },
    twentyFourHour: {
        withSeconds: "HH:mm:ss",
        default: "HH:mm"
    }
};

export const DATE_TIME_PICKER_PLACEHOLDER_KEYS: Record<DateTimePickerPickerType, Record<Exclude<DateTimePickerVariant, "rrule">, keyof NCoreUIKit.Translation>> = {
    "date-time": {
        range: "select-any-date-time-range",
        single: "select-any-date-time"
    },
    date: {
        range: "select-any-date-range",
        single: "select-any-date"
    },
    time: {
        range: "select-any-time-range",
        single: "select-any-time"
    }
};

export const DATE_TIME_PICKER_RANGE_DASH = "–";

export const DATE_TIME_PICKER_RANGE_SEPARATOR = ` ${DATE_TIME_PICKER_RANGE_DASH} `;

export const DATE_TIME_PICKER_EMPTY_VALUE: DateTimePickerValueType = {
    dateRange: undefined,
    dateRule: undefined,
    date: undefined
};

const YEAR_TOKEN_PATTERN = /[-./ ]?(?:YYYY|YY)/;

export const isValidDateTimePickerDate = (date?: Date): date is Date => {
    return date instanceof Date && !Number.isNaN(date.getTime());
};

export const copyDateTimePickerDate = (date?: Date): Date | undefined => {
    if(!date) {
        return undefined;
    }

    return new Date(date.getTime());
};

export const copyDateTimePickerValue = ({
    dateRange,
    dateRule,
    date
}: DateTimePickerValueType): DateTimePickerValueType => {
    return {
        dateRange: dateRange ? {
            start: copyDateTimePickerDate(dateRange.start),
            end: copyDateTimePickerDate(dateRange.end)
        } : undefined,
        date: copyDateTimePickerDate(date),
        dateRule
    };
};

export const getDateTimePickerTitleFormat = ({
    isWorkWith24HoursFormat,
    isWorkWithSeconds,
    pickerType,
    titleFormat
}: DateTimePickerTitleFormatConstantType): string => {
    if(titleFormat) {
        return titleFormat;
    }

    const timeFormat = DATE_TIME_PICKER_TIME_FORMATS[isWorkWith24HoursFormat ? "twentyFourHour" : "twelveHour"][isWorkWithSeconds ? "withSeconds" : "default"];

    if(pickerType === "time") {
        return timeFormat;
    }

    if(pickerType === "date-time") {
        return `${DATE_TIME_PICKER_DATE_FORMAT} ${timeFormat}`;
    }

    return DATE_TIME_PICKER_DATE_FORMAT;
};

export const getDateTimePickerDateText = ({
    activeLocale,
    format,
    date
}: DateTimePickerDateTextConstantType): string => {
    if(!isValidDateTimePickerDate(date)) {
        return "";
    }

    return createMoment({
        locale: activeLocale,
        value: date
    }).format(format);
};

export const getDateTimePickerStartFormat = (format: string): string => {
    const yearMatch = YEAR_TOKEN_PATTERN.exec(format);

    if(!yearMatch || yearMatch.index === 0) {
        return format;
    }

    return `${format.slice(0, yearMatch.index)}${format.slice(yearMatch.index + yearMatch[0].length)}`;
};

export const getDateTimePickerRangeText = ({
    activeLocale,
    dateRange,
    format
}: DateTimePickerRangeTextConstantType): string => {
    const start = dateRange?.start;

    const end = dateRange?.end;

    if(isValidDateTimePickerDate(start) && isValidDateTimePickerDate(end)) {
        const startText = getDateTimePickerDateText({
            format: start.getFullYear() === end.getFullYear() ? getDateTimePickerStartFormat(format) : format,
            activeLocale,
            date: start
        });

        const endText = getDateTimePickerDateText({
            activeLocale,
            date: end,
            format
        });

        const fullStartText = getDateTimePickerDateText({
            activeLocale,
            date: start,
            format
        });

        if(fullStartText === endText) {
            return endText;
        }

        return `${startText}${DATE_TIME_PICKER_RANGE_SEPARATOR}${endText}`;
    }

    if(isValidDateTimePickerDate(start)) {
        return `${getDateTimePickerDateText({
            activeLocale,
            date: start,
            format
        })} ${DATE_TIME_PICKER_RANGE_DASH}`;
    }

    if(isValidDateTimePickerDate(end)) {
        return `${DATE_TIME_PICKER_RANGE_DASH} ${getDateTimePickerDateText({
            activeLocale,
            date: end,
            format
        })}`;
    }

    return "";
};

export const getDateTimePickerPlaceholderKey = ({
    pickerType,
    variant
}: DateTimePickerPlaceholderConstantType): keyof NCoreUIKit.Translation => {
    return DATE_TIME_PICKER_PLACEHOLDER_KEYS[pickerType][variant === "range" ? "range" : "single"];
};

export const getHasDateTimePickerValue = ({
    variant,
    value
}: DateTimePickerHasValueConstantType): boolean => {
    if(variant === "rrule") {
        return !!value.dateRule;
    }

    if(variant === "range") {
        return !!value.dateRange && (!!value.dateRange.start || !!value.dateRange.end);
    }

    return !!value.date;
};

export const getDateTimePickerValueKey = ({
    dateRange,
    dateRule,
    date
}: DateTimePickerValueType): string => {
    return [
        date?.getTime(),
        dateRange?.start?.getTime(),
        dateRange?.end?.getTime(),
        dateRule?.toString()
    ].map((part) => part ?? "").join("|");
};

export const getDateTimePickerResponse = ({
    returnFormat,
    value
}: DateTimePickerResponseConstantType): DateTimePickerResponseType => {
    const {
        dateRange,
        dateRule,
        date
    } = value;

    if(!returnFormat) {
        return copyDateTimePickerValue({
            dateRange,
            dateRule,
            date
        });
    }

    return {
        dateRange: dateRange ? {
            start: dateRange.start ? moment(dateRange.start).format(returnFormat) : undefined,
            end: dateRange.end ? moment(dateRange.end).format(returnFormat) : undefined
        } : undefined,
        date: date ? moment(date).format(returnFormat) : undefined,
        dateRule
    };
};

export const getIsSameDateTimePickerSelection = ({
    isWorkWithSeconds,
    pickerType,
    current,
    next
}: DateTimePickerSelectionConstantType): boolean => {
    if(!current) {
        return false;
    }

    if(pickerType === "date") {
        return moment(next).isSame(current, "day");
    }

    return moment(next).isSame(current, isWorkWithSeconds ? "second" : "minute");
};

const DATE_TIME_PICKER_RANGE_ERROR_UNITS = {
    month: {
        minimumKey: "minimum-selection-number-of-months-required-not-provided",
        maximumKey: "maximum-selection-number-of-months-limit-exceeds",
        diff: "months",
        moment: "month"
    },
    year: {
        minimumKey: "minimum-selection-number-of-years-required-not-provided",
        maximumKey: "maximum-selection-number-of-years-limit-exceeds",
        diff: "years",
        moment: "year"
    },
    day: {
        minimumKey: "minimum-selection-number-of-days-required-not-provided",
        maximumKey: "maximum-selection-number-of-days-limit-exceeds",
        diff: "days",
        moment: "day"
    }
} as const satisfies Record<DateCalendarType, {
    minimumKey: keyof NCoreUIKit.Translation;
    maximumKey: keyof NCoreUIKit.Translation;
    diff: "days" | "months" | "years";
    moment: "day" | "month" | "year";
}>;

export const getDateTimePickerRangeError = ({
    multipleSelectMinimumRequiredDayCount,
    isStartDateSelectionRequired,
    isEndDateSelectionRequired,
    multipleSelectDayLimit,
    calendarType = "day",
    dateRange
}: DateTimePickerRangeErrorConstantType): keyof NCoreUIKit.Translation | undefined => {
    if(isStartDateSelectionRequired && !dateRange?.start) {
        return "start-date-select-is-required";
    }

    if(isEndDateSelectionRequired && !dateRange?.end) {
        return "end-date-select-is-required";
    }

    if(!dateRange?.start || !dateRange.end) {
        return undefined;
    }

    const unit = DATE_TIME_PICKER_RANGE_ERROR_UNITS[calendarType];

    const difference = moment(dateRange.end).startOf(unit.moment).diff(moment(dateRange.start).startOf(unit.moment), unit.diff);

    if(multipleSelectDayLimit && difference >= multipleSelectDayLimit) {
        return unit.maximumKey;
    }

    if(multipleSelectMinimumRequiredDayCount && difference + 1 < multipleSelectMinimumRequiredDayCount) {
        return unit.minimumKey;
    }

    return undefined;
};

export const getDateTimePickerAnchor = (trigger: View | null): View | null => {
    const element: unknown = trigger;

    if(typeof HTMLElement === "undefined" || !(element instanceof HTMLElement) || !element.parentElement) {
        return trigger;
    }

    return element.parentElement as unknown as View;
};
